import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Skeleton, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  EyeOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  MinusCircleFilled,
  ReloadOutlined,
  StopOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { paths } from 'routes/routes';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { findNav } from 'layouts/nav-config';
import { colors, toneColors } from 'theme/tokens';
import {
  FeaturePending,
  LoadFailed,
  TABULAR,
  apiErrorText,
  fmtDateTime,
  fmtNumber,
  isMissingEndpoint,
  unwrap,
} from './common';
import MembersDrawer from './members-drawer';
import {
  AccessTone,
  ManagedAt,
  MemberBadge,
  MemberTypeAccessLine,
  MemberTypeCard,
  MemberTypesOverview,
} from './types';
import styles from './styles.module.scss';

/**
 * Trang "Nhóm người" (/access/groups) — ADMIN + MOD, chỉ xem.
 * docs/to-chuc-lai-nhom-quyen/00-ban-do-va-phuong-an-2026-09-29.md §4.2.
 * Phần "Được làm gì" do máy chủ tính từ chính luật đang chạy, trang chỉ hiện lại.
 */

const TITLE = 'Nhóm người';
// Studio / nhóm nhảy hiện quản lý ở mục menu "Nhóm quản lý" (chưa có mục "Studio" riêng).
const STUDIO_PATH = '/domestic-management';
const STUDIO_MENU_LABEL = 'Nhóm quản lý';

type Phase = 'loading' | 'ready' | 'pending' | 'error';

// Không chỉ dựa vào màu: mỗi mức có hình riêng và chữ đọc được (title / aria-label).
const TONE: Record<AccessTone, { icon: React.ReactNode; color: string; text: string }> = {
  // aria-hidden trên icon: antd tự gắn aria-label tiếng Anh ("check-circle"), chữ đọc nằm ở thẻ bọc.
  yes: { icon: <CheckCircleFilled aria-hidden />, color: colors.successInk, text: 'Được' },
  limited: {
    icon: <MinusCircleFilled aria-hidden />,
    color: colors.warningInk,
    text: 'Có giới hạn',
  },
  no: { icon: <StopOutlined aria-hidden />, color: colors.textSecondary, text: 'Không' },
};

const internalPath = (path?: string) => (path ? (path.startsWith('/') ? path : `/${path}`) : '/');

// Tab chỉ Admin thấy (TabbedPage `hidden: !isAdmin`) trên trang mà MOD vẫn mở được:
// pages/data-import/index.tsx tab "quyen" (Người được nhập). MOD bấm vào chỉ gặp tab khác
// hoặc màn "chưa được cấp quyền nhập dữ liệu".
const ADMIN_ONLY_TABS: Record<string, string[]> = { '/data-import': ['quyen'] };

/** Trang (hoặc tab) chỉ Admin mở được (adminOnly trong nav-config); MOD mở ra chỉ gặp màn "chỉ dành cho Admin". */
const isAdminOnlyPath = (path?: string) => {
  const [route, query = ''] = internalPath(path).split('#')[0].split('?');
  if (findNav(route)?.item.adminOnly) return true;
  const tab = new URLSearchParams(query).get('tab');
  return !!tab && (ADMIN_ONLY_TABS[route] || []).includes(tab);
};

/**
 * Link sang trang khác. Người xem không phải Admin (MOD, hoặc chưa biết vai trò) mà trang đích
 * chỉ Admin mở được thì chỉ hiện chữ, kèm "(chỉ Admin)", không đưa họ tới màn bị chặn.
 */
const PageLink = ({
  to,
  admin,
  className,
  style,
  children,
}: {
  to: string;
  admin: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) =>
  admin || !isAdminOnlyPath(to) ? (
    <Link to={internalPath(to)} className={className} style={style}>
      {children}
    </Link>
  ) : (
    <span className={className} style={{ ...style, color: colors.textSecondary }}>
      {children} (chỉ Admin)
    </span>
  );

const ManagedLink = ({ at, admin }: { at?: ManagedAt | null; admin: boolean }) =>
  at?.path ? (
    <PageLink to={at.path} admin={admin} className={styles.managedAt}>
      Chỉnh ở: {at.label}
    </PageLink>
  ) : null;

const AccessLine = ({ line, admin }: { line: MemberTypeAccessLine; admin: boolean }) => {
  const tone = TONE[line.tone] || TONE.no;
  return (
    <li className={styles.accessLine}>
      <span
        className={styles.toneIcon}
        style={{ color: tone.color }}
        role="img"
        aria-label={tone.text}
        title={tone.text}
      >
        {tone.icon}
      </span>
      <span className={styles.accessBody}>
        <span className={styles.accessFeature}>{line.label}</span>
        <span className={styles.accessValue}> · {line.value}</span>{' '}
        <ManagedLink at={line.managedAt} admin={admin} />
      </span>
    </li>
  );
};

const GroupCard = ({
  group,
  canList,
  onList,
}: {
  group: MemberTypeCard;
  canList: boolean;
  onList: () => void;
}) => (
  <section className={styles.card} aria-label={group.label}>
    <div className={styles.cardHead}>
      <div style={{ minWidth: 0 }}>
        <h2 className={styles.cardTitle}>{group.label}</h2>
        <p className={styles.cardDescription}>{group.description}</p>
      </div>
      <div className={styles.count}>
        <span className={styles.countNumber}>{fmtNumber(group.count)}</span>
        <span className={styles.countUnit}>người</span>
      </div>
    </div>
    <p className={styles.rule}>
      <span className={styles.ruleLabel}>Nhận biết</span>
      {group.rule}
    </p>
    <h3 className={styles.accessTitle}>Được làm gì</h3>
    <ul className={styles.accessList}>
      {(group.access || []).map((line) => (
        <AccessLine key={line.feature} line={line} admin={canList} />
      ))}
    </ul>
    {canList && (
      <div className={styles.cardFoot}>
        <Button size="small" icon={<UnorderedListOutlined />} onClick={onList}>
          Xem danh sách
        </Button>
      </div>
    )}
  </section>
);

const SkeletonGrid = () => (
  <div className={styles.grid} aria-busy="true" aria-label="Đang tải các nhóm người">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className={styles.card}>
        <Skeleton active title={{ width: '50%' }} paragraph={{ rows: 9 }} />
      </div>
    ))}
  </div>
);

const Ideas = ({ admin }: { admin: boolean }) => (
  <ul className={styles.ideas}>
    <li className={styles.idea}>
      <span
        className={styles.ideaIcon}
        style={{ color: toneColors.accent.ink, background: toneColors.accent.soft }}
        aria-hidden
      >
        <IdcardOutlined />
      </span>
      <span className={styles.ideaText}>
        <strong>Bạn là ai → Nhóm người.</strong> Mỗi người thuộc đúng một nhóm ở trang này.
      </span>
    </li>
    <li className={styles.idea}>
      <span
        className={styles.ideaIcon}
        style={{ color: toneColors.warning.ink, background: toneColors.warning.soft }}
        aria-hidden
      >
        <KeyOutlined />
      </span>
      <span className={styles.ideaText}>
        <strong>Được làm gì → Quyền.</strong> Đặt theo nhóm, không đặt riêng từng người. Xem ở{' '}
        <Link to={paths.accessPermissions}>Quyền hạn</Link>.
      </span>
    </li>
    <li className={styles.idea}>
      <span
        className={styles.ideaIcon}
        style={{ color: toneColors.info.ink, background: toneColors.info.soft }}
        aria-hidden
      >
        <TeamOutlined />
      </span>
      <span className={styles.ideaText}>
        <strong>Ở đội nào → Studio, nhóm nhảy.</strong> Quản lý ở mục{' '}
        <PageLink to={STUDIO_PATH} admin={admin}>
          {STUDIO_MENU_LABEL}
        </PageLink>
        , không phải ở đây.
      </span>
    </li>
  </ul>
);

const AccessGroupsPage = () => {
  const { isAdmin, loaded } = useCurrentRoles();
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<MemberTypesOverview | null>(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [listing, setListing] = useState<MemberTypeCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRefreshing(true);
    AxiosUtils.get(API.memberTypesOverview)
      .then((res: any) => {
        if (cancelled) return;
        const next = unwrap<MemberTypesOverview>(res, (body) => Array.isArray(body?.types));
        if (!next) {
          setError('Máy chủ trả về dữ liệu không đọc được. Vui lòng thử lại.');
          setPhase('error');
          return;
        }
        setView(next);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        if (isMissingEndpoint(err)) {
          setPhase('pending');
          return;
        }
        setError(apiErrorText(err, {}, 'Không tải được danh sách nhóm người.'));
        setPhase('error');
      })
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = () => {
    setPhase((current) => (current === 'ready' ? current : 'loading'));
    setAttempt((n) => n + 1);
  };

  const canList = loaded && isAdmin;

  const badgeColumns: ColumnsType<MemberBadge> = [
    {
      title: 'Quyền riêng',
      key: 'label',
      width: 220,
      render: (_, row) => <strong>{row.label}</strong>,
    },
    {
      title: 'Dùng để làm gì',
      key: 'description',
      render: (_, row) => <span style={{ overflowWrap: 'anywhere' }}>{row.description}</span>,
    },
    {
      title: 'Số người',
      key: 'count',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.count === null || row.count === undefined ? (
          <Tooltip title="Chưa có dữ liệu">
            <span tabIndex={0} aria-label="Chưa có dữ liệu" style={{ color: colors.textSecondary }}>
              —
            </span>
          </Tooltip>
        ) : (
          <span style={TABULAR}>{fmtNumber(row.count)}</span>
        ),
    },
    {
      title: 'Quản lý ở',
      key: 'managedAt',
      width: 260,
      render: (_, row) =>
        row.managedAt?.path ? (
          <PageLink to={row.managedAt.path} admin={canList} style={{ fontWeight: 600 }}>
            {row.managedAt.label}
          </PageLink>
        ) : (
          <span style={{ color: colors.textSecondary }}>—</span>
        ),
    },
  ];

  let body: React.ReactNode;
  if (phase === 'loading') {
    body = (
      <>
        <div className={styles.summary} />
        <SkeletonGrid />
      </>
    );
  } else if (phase === 'pending') {
    body = <FeaturePending onRetry={retry} />;
  } else if (phase === 'error' || !view) {
    body = <LoadFailed title="Không tải được trang Nhóm người" text={error} onRetry={retry} />;
  } else {
    const badges = Array.isArray(view.badges) ? view.badges : [];
    body = (
      <>
        <div className={styles.summary}>
          <span>
            Tổng <strong>{fmtNumber(view.total)}</strong> tài khoản, chia vào{' '}
            <strong>{view.types.length}</strong> nhóm. Số liệu lúc {fmtDateTime(view.generatedAt)}.
          </span>
          <span className={styles.readOnly}>
            <EyeOutlined aria-hidden />
            Trang chỉ để xem.
            <Button
              size="small"
              type="text"
              icon={<ReloadOutlined />}
              loading={refreshing}
              onClick={retry}
            >
              Tải lại
            </Button>
          </span>
        </div>
        <div className={styles.grid}>
          {view.types.map((group) => (
            <GroupCard
              key={group.key}
              group={group}
              canList={canList}
              onList={() => setListing(group)}
            />
          ))}
        </div>

        <h2 className={styles.sectionTitle}>Quyền cấp riêng cho từng người</h2>
        <p className={styles.sectionHint}>
          Ngoài nhóm chính, Admin có thể cấp thêm cho từng người các quyền dưới đây. Muốn cấp hay
          thu hồi thì vào đúng trang ở cột “Quản lý ở”.
        </p>
        <div className="custom-table">
          <Table<MemberBadge>
            rowKey="key"
            size="middle"
            pagination={false}
            columns={badgeColumns}
            dataSource={badges}
            locale={{ emptyText: 'Chưa có quyền riêng nào.' }}
            scroll={badges.length ? { x: 860 } : undefined}
          />
        </div>

        <div className={styles.footNote}>
          <InfoCircleOutlined style={{ color: colors.infoInk, marginTop: 3 }} aria-hidden />
          <span>
            Nhóm nhảy và Studio là <strong>ĐỘI</strong>, không phải nhóm người. Quản lý ở mục{' '}
            <PageLink to={STUDIO_PATH} admin={canList}>
              {STUDIO_MENU_LABEL}
            </PageLink>
            .
          </span>
        </div>
      </>
    );
  }

  return (
    <div>
      <PageTitle title={TITLE} />
      {phase !== 'pending' && <Ideas admin={canList} />}
      {body}
      <MembersDrawer group={canList ? listing : null} onClose={() => setListing(null)} />
    </div>
  );
};

export default AccessGroupsPage;
